/**
 * Recently Viewed Products Module
 * Handles tracking and displaying recently viewed products
 */

class RecentlyViewed {
    constructor() {
      this.storageKey = 'recently_viewed';
      this.maxProducts = this.getMaxProducts();
      this.init();
    }
  
    /**
     * Get maximum number of products from data attribute
     */
    getMaxProducts() {
      const element = document.querySelector('recently-viewed');
      return parseInt(element?.getAttribute("data-products-count") || "8", 10);
    }
  
    /**
     * Escape quotes in strings to prevent JavaScript injection
     */
    escapeQuotes(str) {
      if (typeof str !== 'string') return str;
      return str.replace(/'/g, "\\'").replace(/"/g, '\\"');
    }
  
    /**
     * Get existing recently viewed data from sessionStorage
     */
    getStoredData() {
      try {
        const data = sessionStorage.getItem(this.storageKey);
        return data ? JSON.parse(data) : [];
      } catch (error) {
        return [];
      }
    }
  
    /**
     * Save data to sessionStorage
     */
    saveData(data) {
      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify(data));
      } catch (error) {
        // Silently handle storage errors
      }
    }
  
    /**
     * Add current product to recently viewed list
     */
    addCurrentProduct() {
      if (!pdpData?.productTitle?.trim()) {
        return;
      }
  
      const currentProduct = {
        ...pdpData,
        productTitle: this.escapeQuotes(pdpData.productTitle.trim())
      };
      const existingData = this.getStoredData();
      const currentTitle = currentProduct.productTitle;
  
      // Check if product is already in the list
      const isDuplicate = existingData.some(item => item.productTitle === currentTitle);
  
      if (isDuplicate) {
        return;
      }
  
      // Add new product to the beginning
      const updatedData = [currentProduct, ...existingData];
  
      // Limit to max products
      if (updatedData.length > this.maxProducts) {
        updatedData.splice(this.maxProducts);
      }
  
      this.saveData(updatedData);
    }
  
    /**
     * Generate HTML for recently viewed products
     */
    generateProductHTML(product) {
      const escapedTitle = this.escapeQuotes(product.productTitle || '');
      return `
        <div class="resource-list__item">
          <product-card class="product-card border-style ">
            <a class="contents" href="${product.productUrl}" title="${escapedTitle}" aria-label="${escapedTitle}" ref="productCardLink">
              <div class="product-media-container media-fit product-media-container--image">
                <div class="product-media">
                  ${product.productImg}
                </div>
              </div>
            </a>
          </product-card>
        </div>
      `;
    }
  
    /**
     * Render recently viewed products to the page
     */
    renderProducts() {
      const products = this.getStoredData();
  
      if (!products.length) {
        return;
      }
  
      const limitedProducts = products.slice(0, this.maxProducts);
  
      // Set data-has-recently-viewed to true on the recently-viewed element
      const recentlyViewedElement = document.querySelector('recently-viewed');
      if (recentlyViewedElement) {
        recentlyViewedElement.setAttribute('data-has-recently-viewed', 'true');
      }
  
      // Handle grid layout
      const gridElements = document.querySelectorAll('.js-recentPdpBlockGrid');
      gridElements.forEach(element => {
        this.renderGrid(element, limitedProducts);
      });
  
      // Handle carousel layout
      const carouselElements = document.querySelectorAll('.js-recentPdpBlockCarousel');
      carouselElements.forEach(element => {
        this.renderCarouselSlides(element, limitedProducts);
      });
    }
  
    /**
     * Render products in grid layout
     */
    renderGrid(element, products) {
      const html = products
        .map(product => this.generateProductHTML(product))
        .join('');
  
      element.innerHTML = html;
    }
  
    /**
     * Render products as slideshow slides for carousel
     */
    renderCarouselSlides(element, products) {
      const slides = products.map((product, index) => {
        const productHtml = this.generateProductHTML(product);
        return `
          <slideshow-slide
            ref="slides[]"
            aria-hidden="${index === 0 ? 'false' : 'true'}"
            style="view-timeline-name: --slide-${index}; --product-media-fit: cover;"
            slide-id="recently-viewed-${index}"
            class="resource-list__slide"
          >
            ${productHtml}
          </slideshow-slide>
        `;
      }).join('');
  
      element.innerHTML = slides;
    }
  
    /**
     * Initialize the recently viewed functionality
     */
    init() {
      // Add current product to recently viewed
      this.addCurrentProduct();
  
      // Render on DOM ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.renderProducts());
      } else {
        this.renderProducts();
      }
    }
  }
  
  // Initialize the module
  new RecentlyViewed();